import { createSlice } from "@reduxjs/toolkit";
import { ApiStatus } from "../../utils/api.types";
import { AInvoiceSliceState } from "../../utils/invoice.types";
import { AReportSliceState } from "../../utils/report.types";
import {
    fetchReport,
    fetchPartyLedgerReport,
    downloadPartyLedgerReport,
    fetchTdsPayableReport,
    fetchTdsReceivableReport,
    fetchTcsPayableReport,
    fetchTcsReceivableReport,
    fetchLowStockSummaryReport,
    downloadLowStockSummaryReport,
    fetchRateListReport,
    downloadRateListReport,
    fetchProfitLoss,
    downloadProfitLoss,
    fetchBalanceSheet,
    downloadBalanceSheet,
    fetchBalanceSheetPartyWise,
    downloadBalanceSheetPartyWise,
    fetchPartyBalances,
    searchParties,
    fetchJournalEntriesPartyReport,
    fetchAccountLedgers,
    fetchJournalEntriesPartyLedgerReport,
    fetchDayBookReport,
    fetchCashAndBankReport
} from "./reportThunk";

/**
 * Report slice initial state.
 */
const initialState: AReportSliceState = {
    reportsData: [],
    profitLossData: null,
    balanceSheetData: null,
    balanceSheetPartyWiseData: null,
    partyBalances: [],
    partyBalancesPagination: null,
    partyBalancesSummary: null,
    searchedParties: [],
    accountLedgers: [],
    dayBookData: null,
    cashAndBankData: null,
    partyLedgerReportData: null,
    errorMessage: '',
    status: ApiStatus.IDLE
}

/**
 * Report Slice.
*/
const reportSlice = createSlice({
    name: 'report',
    initialState: initialState,
    reducers: {
        clearReportsData: (state) => {
            state.reportsData = [];
            state.status = ApiStatus.IDLE;
            state.errorMessage = '';
        },
        clearPartyBalances: (state) => {
            state.partyBalances = [];
            state.partyBalancesPagination = null;
            state.partyBalancesSummary = null;
            state.status = ApiStatus.IDLE;
            state.errorMessage = '';
        },
        clearSearchedParties: (state) => {
            state.searchedParties = [];
        },
        clearPartyLedgerReportData: (state) => {
            state.partyLedgerReportData = null;
            state.status = ApiStatus.IDLE;
            state.errorMessage = '';
        },
        clearDayBookData: (state) => {
            state.dayBookData = null;
            state.status = ApiStatus.IDLE;
            state.errorMessage = '';
        },
        clearCashAndBankData: (state) => {
            state.cashAndBankData = null;
            state.status = ApiStatus.IDLE;
            state.errorMessage = '';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Reports
            .addCase(fetchReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.reportData || [];
                state.errorMessage = '';
            })
            .addCase(fetchReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Fetch fetchPartyLedgerReport
            .addCase(fetchPartyLedgerReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchPartyLedgerReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
            })
            .addCase(fetchPartyLedgerReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Fetch fetchPartyLedgerReport
            .addCase(downloadPartyLedgerReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(downloadPartyLedgerReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
            })
            .addCase(downloadPartyLedgerReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // TDS Payable Report
            .addCase(fetchTdsPayableReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchTdsPayableReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.reportData || [];
                state.errorMessage = '';
            })
            .addCase(fetchTdsPayableReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // TDS Receivable Report
            .addCase(fetchTdsReceivableReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchTdsReceivableReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.reportData || [];
                state.errorMessage = '';
            })
            .addCase(fetchTdsReceivableReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // TCS Payable Report
            .addCase(fetchTcsPayableReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchTcsPayableReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.reportData || [];
                state.errorMessage = '';
            })
            .addCase(fetchTcsPayableReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // TCS Receivable Report
            .addCase(fetchTcsReceivableReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchTcsReceivableReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.reportData || [];
                state.errorMessage = '';
            })
            .addCase(fetchTcsReceivableReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Low Stock Summary Report
            .addCase(fetchLowStockSummaryReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchLowStockSummaryReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.data?.items || [];
                state.errorMessage = '';
            })
            .addCase(fetchLowStockSummaryReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Download Low Stock Summary Report
            .addCase(downloadLowStockSummaryReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(downloadLowStockSummaryReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
            })
            .addCase(downloadLowStockSummaryReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Rate List Report
            .addCase(fetchRateListReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchRateListReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.data?.items || [];
                state.errorMessage = '';
            })
            .addCase(fetchRateListReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Download Rate List Report
            .addCase(downloadRateListReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(downloadRateListReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
            })
            .addCase(downloadRateListReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Profit & Loss Statement
            .addCase(fetchProfitLoss.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchProfitLoss.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.profitLossData = action?.payload.data || null;
                state.errorMessage = '';
            })
            .addCase(fetchProfitLoss.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Download Profit & Loss Statement - no state updates needed for downloads
            // Balance Sheet
            .addCase(fetchBalanceSheet.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchBalanceSheet.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.balanceSheetData = action?.payload.data || null;
                state.errorMessage = '';
            })
            .addCase(fetchBalanceSheet.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Download Balance Sheet - no state updates needed for downloads
            // Balance Sheet Party-wise
            .addCase(fetchBalanceSheetPartyWise.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchBalanceSheetPartyWise.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.balanceSheetPartyWiseData = action?.payload.data || null;
                state.errorMessage = '';
            })
            .addCase(fetchBalanceSheetPartyWise.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Download Balance Sheet Party-wise - no state updates needed for downloads
            // Fetch Party Balances
            .addCase(fetchPartyBalances.pending, (state, action) => {
                // Only set loading status for first page, not for pagination
                const isFirstPage = action.meta.arg.page === undefined || action.meta.arg.page === 1;
                if (isFirstPage) {
                    state.status = ApiStatus.LOADING;
                }
            })
            .addCase(fetchPartyBalances.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                // Check if it's first page or pagination
                const isFirstPage = action.meta.arg.page === undefined || action.meta.arg.page === 1;
                const newParties = action?.payload.data?.parties || [];

                if (isFirstPage) {
                    // Replace all parties for first page
                    state.partyBalances = newParties;
                } else {
                    // Append only new parties for subsequent pages (avoid duplicates)
                    const existingIds = new Set(state.partyBalances.map(p => p.partyId));
                    const uniqueNewParties = newParties.filter((p: any) => !existingIds.has(p.partyId));
                    state.partyBalances = [...state.partyBalances, ...uniqueNewParties];
                }
                state.partyBalancesPagination = action?.payload.data?.pagination || null;
                state.partyBalancesSummary = action?.payload.data?.summary || null;
                state.errorMessage = '';
            })
            .addCase(fetchPartyBalances.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Search Parties
            .addCase(searchParties.pending, (state, action) => {
                // Don't change the main status for search
            })
            .addCase(searchParties.fulfilled, (state, action: any) => {
                state.searchedParties = action?.payload.data || [];
            })
            .addCase(searchParties.rejected, (state, action) => {
                state.searchedParties = [];
            })
            // Journal Entries Party Report
            .addCase(fetchJournalEntriesPartyReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchJournalEntriesPartyReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.reportsData = action?.payload.reportData || [];
                state.errorMessage = '';
            })
            .addCase(fetchJournalEntriesPartyReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Fetch Account Ledgers
            .addCase(fetchAccountLedgers.pending, (state, action) => {
                // Don't change main status for ledger list fetch
            })
            .addCase(fetchAccountLedgers.fulfilled, (state, action: any) => {
                state.accountLedgers = action?.payload.data || [];
            })
            .addCase(fetchAccountLedgers.rejected, (state, action) => {
                state.accountLedgers = [];
            })
            // Journal Entries Party-Wise Ledger Report
            .addCase(fetchJournalEntriesPartyLedgerReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchJournalEntriesPartyLedgerReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.partyLedgerReportData = action?.payload.data || null;
                state.errorMessage = '';
            })
            .addCase(fetchJournalEntriesPartyLedgerReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })
            // Day Book Report
            .addCase(fetchDayBookReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchDayBookReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.dayBookData = action?.payload.data || null;
                state.errorMessage = '';
            })
            .addCase(fetchDayBookReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to fetch Day Book report';
            })
            // Cash and Bank Report
            .addCase(fetchCashAndBankReport.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchCashAndBankReport.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.cashAndBankData = action?.payload.data || null;
                state.errorMessage = '';
            })
            .addCase(fetchCashAndBankReport.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message || 'Failed to fetch Cash and Bank report';
            })
    }
});

export const { clearReportsData, clearPartyBalances, clearSearchedParties, clearPartyLedgerReportData, clearDayBookData, clearCashAndBankData } = reportSlice.actions;
export default reportSlice.reducer;
